// Action Inbox.
//
// Layout: filters (left) + action list (right). Each row is wrapped in a
// <Link> that navigates to /actions/:code where the full detail page
// (with updates timeline, resolve/update/flag controls) lives.
//
// Phase 6 polish:
//   - Per-tab count badges — exact counts (#960): "mine" comes from
//     get_my_badge_counts() (it is the one owner-scoped tab, so the RPC's
//     owner-scoped count matches what the tab shows); every other tab —
//     including "Overdue" — from a limit:1 listActions call reading
//     total_count, i.e. the exact same filter/scope the tab's own contents
//     use. Reloaded on mount and on realtime changes.
//   - Search box at the top (client-side filter on title/owner/code).
//   - Empty state copy: "Nothing here yet — try another tab or change the filter".
//
// Phase 7:
//   - Inline status pill on each row (open → in_progress → resolved).
//   - "By person" filter chips above the list — pick a staff member to scope
//     the current tab to actions they own. Admins use this to triage; staff
//     can also use it to see a teammate's queue.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { Loader2, AlertCircle, Search, Plus } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ActionRow } from "@/components/ActionRow";
import { NewActionDialog } from "@/components/NewActionDialog";
import { ReadOnlyBanner, PermissionGate } from "@/components/PermissionGate";
import { cn } from "@/lib/cn";
import { withFrom } from "@/lib/nav";
import { useMcp, mcp, type ActionFilter, type Action } from "@/lib/mcp";
import { supabase } from "@/lib/supabase";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { useListStateRestore } from "@/hooks/useListStateRestore";

interface StaffOption {
  id: string;
  display_name: string;
  open_count: number;
}

const ALL_PERSONS = "__all__";

// "proposed" is a UI-only tab — the SP3 shadow-mode resolver writes
// `proposed_resolution` rows into ops.action_updates with a jsonb body
// holding {confidence, evidence_quote, …}. There's no server-side
// filter in mcp_list_actions for this yet; we fetch the candidate
// action_ids directly off ops.action_updates and then resolve them.
type ProposedTab = "proposed";
type TabKey = ActionFilter | ProposedTab;

const FILTERS: Array<{ key: TabKey; label: string }> = [
  { key: "mine", label: "Mine" },
  { key: "all_open", label: "All Open" },
  { key: "overdue", label: "Overdue" },
  { key: "josh_flagged", label: "Josh-Flagged" },
  { key: "proposed", label: "Proposed" },
  { key: "resolved", label: "Resolved" },
];

// Per-tab count badges (#960). Exported for the unit tests in
// `__tests__/loadTabCounts.test.ts`, which pin WHICH source each tab's badge
// reads from — the thing that regressed in review on this very PR.
//
// The rule: **a badge must be computed with the same filter and the same
// scope as the tab's own contents.**
//
//   - "mine" is the only owner-scoped tab (`effectiveOwnerId` is pinned to the
//     caller for it), so it reads `get_my_badge_counts()`'s owner-scoped
//     `/actions.kinds.open` — one round trip, no row cap.
//   - every other tab, "overdue" included, fetches `limit: 1` and reads
//     `total_count` from the identical `listActions({ filter })` call that
//     populates the tab. `ops.actions` RLS is `using (true)` for staff and the
//     tab passes no `owner_user_id` unless a person chip is picked, so the tab
//     genuinely lists ALL staff members' rows; `get_my_badge_counts()`'s
//     `overdue` kind is owner-scoped (and uses a different threshold — no
//     3-day grace period, includes `blocked`), so feeding it into this badge
//     made the number visibly disagree with the tab on any multi-person team.
//     That RPC kind stays as-is for the dashboard's personal "My overdue
//     actions" card and the future personal-badge UI (phase 2a) — it just
//     must not source this sidebar count.
//   - either way the count is exact: no `actions.length` capped at
//     `limit: 100`, so no more "100+" display cap.
export async function loadTabCounts(
  userId: string | null,
): Promise<Partial<Record<TabKey, number>>> {
  if (userId === null) return {};

  const [badgeCounts, otherEntries] = await Promise.all([
    mcp.getMyBadgeCounts().catch(() => null),
    Promise.all(
      FILTERS.filter((f) => f.key !== "mine").map(async (f) => {
        if (f.key === "proposed") {
          // Pending = no confirmed_at / rejected_at in the jsonb body.
          // `.is('body->confirmed_at', null)` works for both NULL body and a
          // jsonb object missing the key (PostgREST returns NULL for absent
          // jsonb keys via the `->` operator). Already an exact head count —
          // no limit:100 pattern here to begin with.
          const { count } = await supabase
            .schema("ops")
            .from("action_updates")
            .select("id", { count: "exact", head: true })
            .eq("update_type", "proposed_resolution")
            .is("body->confirmed_at", null)
            .is("body->rejected_at", null);
          return [f.key, count ?? 0] as const;
        }
        try {
          const res = await mcp.listActions({ filter: f.key, limit: 1 });
          return [f.key, res.total_count] as const;
        } catch {
          return [f.key, 0] as const;
        }
      }),
    ),
  ]);

  const actionsKinds = badgeCounts?.surfaces?.["/actions"]?.kinds;
  return Object.fromEntries([
    ...otherEntries,
    ["mine", actionsKinds?.open ?? 0],
  ]) as Partial<Record<TabKey, number>>;
}

export function ActionsInboxPage() {
  const location = useLocation();
  const listKey = location.pathname + location.search;
  useListStateRestore({ key: listKey });
  const [filter, setFilter] = useState<TabKey>("mine");
  const [personFilter, setPersonFilter] = useState<string>(ALL_PERSONS);
  const [userId, setUserId] = useState<string | null>(null);
  const [searchRaw, setSearchRaw] = useState("");
  const [newActionOpen, setNewActionOpen] = useState(false);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  // Load staff + their open action counts for the "by person" chip row.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: staff }, { data: actions }] = await Promise.all([
        supabase.schema("staff").from("users").select("id, display_name").eq("is_active", true),
        supabase.schema("ops").from("actions")
          .select("owner_user_id")
          .eq("status", "open")
          .not("owner_user_id", "is", null),
      ]);
      if (cancelled) return;
      const counts = new Map<string, number>();
      (actions ?? []).forEach((a) => {
        const id = (a as { owner_user_id: string | null }).owner_user_id;
        if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
      });
      const opts: StaffOption[] = ((staff ?? []) as Array<{ id: string; display_name: string | null }>)
        .map((s) => ({
          id: s.id,
          display_name: s.display_name ?? "(unnamed)",
          open_count: counts.get(s.id) ?? 0,
        }))
        .sort((a, b) => b.open_count - a.open_count || a.display_name.localeCompare(b.display_name));
      setStaffOptions(opts);
    })();
    return () => { cancelled = true; };
  }, []);

  // Per-tab count badges (#960) — see `loadTabCounts` above for which source
  // each tab reads and why. Reuses the same useMcp hook the main action list
  // below uses, rather than a hand-rolled fetch effect, so the
  // fetch/loading/refresh wiring is identical to every other page in this file
  // and never calls setState synchronously inside an effect
  // (`react-hooks/set-state-in-effect`) — the counts are reloaded whenever the
  // realtime subscription below fires, not only on userId change.
  const {
    data: countsData, loading: countsMcpLoading, refresh: refreshCounts,
  } = useMcp(() => loadTabCounts(userId), [userId]);
  const counts = countsData ?? {};
  // Before `userId` resolves (the initial `supabase.auth.getUser()` call
  // above hasn't settled), keep the badges in their "..." state rather than
  // briefly showing all-zero counts for a fetch that hasn't been asked for
  // the real user yet.
  const countsLoading = userId === null || countsMcpLoading;

  // Effective owner filter:
  // - "mine" tab: locked to the current user (the chip row is hidden in that case).
  // - any other tab + a person chip selected: filter to that user.
  // - any other tab + no chip: no owner filter.
  const effectiveOwnerId: string | undefined =
    filter === "mine"
      ? (userId ?? undefined)
      : (personFilter !== ALL_PERSONS ? personFilter : undefined);

  const { data, error, loading, refresh } = useMcp(
    async () => {
      // SP3 — "proposed" tab is UI-only. Fetch pending proposal rows then
      // resolve the parent actions in one shot. Server-side filter parity
      // can come later if this tab graduates from shadow-mode.
      if (filter === "proposed") {
        const { data: rows, error } = await supabase
          .schema("ops")
          .from("action_updates")
          .select("action_id, created_at")
          .eq("update_type", "proposed_resolution")
          .is("body->confirmed_at", null)
          .is("body->rejected_at", null)
          .order("created_at", { ascending: false })
          .limit(100);
        if (error) throw error;
        const ids = Array.from(new Set((rows ?? []).map((r) => r.action_id as string)));
        if (ids.length === 0) {
          return { actions: [] as Action[], next_cursor: null, count: 0, filter: "proposed" as const };
        }
        const { data: acts, error: actsErr } = await supabase
          .schema("ops")
          .from("actions")
          .select("*")
          .in("id", ids);
        if (actsErr) throw actsErr;
        // Honour the optional person chip on the proposed tab too.
        const filtered = effectiveOwnerId
          ? (acts ?? []).filter((a) => (a as { owner_user_id?: string | null }).owner_user_id === effectiveOwnerId)
          : (acts ?? []);
        // Preserve the proposal-recency order so the freshest review sits
        // at the top.
        const orderMap = new Map(ids.map((id, idx) => [id, idx]));
        const ordered = [...filtered].sort((a, b) => {
          const ai = orderMap.get((a as { id: string }).id) ?? 99999;
          const bi = orderMap.get((b as { id: string }).id) ?? 99999;
          return ai - bi;
        });
        return {
          actions: ordered as unknown as Action[],
          next_cursor: null,
          count: ordered.length,
          filter: "proposed" as const,
        };
      }
      return mcp.listActions({
        filter,
        ...(effectiveOwnerId ? { owner_user_id: effectiveOwnerId } : {}),
        limit: 100,
      });
    },
    [filter, userId, personFilter],
  );

  useRealtimeRefresh(
    [
      { schema: "ops", table: "actions" },
      { schema: "ops", table: "action_updates" },
    ],
    useCallback(() => {
      refresh();
      refreshCounts();
    }, [refresh, refreshCounts]),
  );

  const filtered = useMemo<Action[]>(() => {
    const list = (data?.actions ?? []) as Action[];
    const q = searchRaw.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => {
      const haystack = [
        a.title,
        a.code,
        a.owner_label,
        a.description,
      ].filter(Boolean).map((s) => String(s).toLowerCase()).join(" ");
      return haystack.includes(q);
    });
  }, [data, searchRaw]);

  return (
    <div className="space-y-4">
      <ReadOnlyBanner />
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Actions</h2>
        <p className="text-sm text-muted-foreground">
          Open follow-ups and proposals across every module, in one inbox.
        </p>
      </div>
      <div className="grid h-[calc(100vh-7rem)] grid-cols-1 gap-4 md:grid-cols-[14rem_minmax(0,1fr)]">
        <FiltersSidebar
          current={filter}
          counts={counts}
          countsLoading={countsLoading}
          onChange={setFilter}
        />

        <Card className="overflow-hidden">
          <div className="border-b p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">Actions</p>
              <PermissionGate>
                <Button size="sm" onClick={() => setNewActionOpen(true)}>
                  <Plus className="h-4 w-4" /> New action
                </Button>
              </PermissionGate>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchRaw}
                onChange={(e) => setSearchRaw(e.target.value)}
                placeholder="Filter by title, owner, or code..."
                className="pl-9"
                aria-label="Filter actions"
              />
            </div>

            {filter !== "mine" && staffOptions.length > 0 && (
              <PersonChips
                options={staffOptions}
                value={personFilter}
                onChange={setPersonFilter}
              />
            )}
          </div>

          <CardContent className="h-full overflow-y-auto p-3">
            {loading && (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading actions…
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div>
                  <p className="font-medium">Action list unavailable</p>
                  <p className="text-xs opacity-80">{error.message}</p>
                </div>
              </div>
            )}
            {!loading && !error && data && (
              <ActionList actions={filtered} hasSearch={searchRaw.trim().length > 0} onChanged={refresh} listKey={listKey} />
            )}
          </CardContent>
        </Card>
      </div>
      <NewActionDialog
        open={newActionOpen}
        onOpenChange={setNewActionOpen}
        onCreated={refresh}
      />
    </div>
  );
}

export function FiltersSidebar({ current, counts, countsLoading, onChange }: {
  current: TabKey;
  counts: Partial<Record<TabKey, number>>;
  countsLoading: boolean;
  onChange: (f: TabKey) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="px-2 text-xs uppercase tracking-wide text-muted-foreground">Inbox</h3>
      <nav className="flex flex-col gap-1">
        {FILTERS.map((f) => {
          const c = counts[f.key];
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => onChange(f.key)}
              className={cn(
                "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                current === f.key
                  ? "bg-primary/10 font-medium text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <span>{f.label}</span>
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[11px] tabular-nums",
                  current === f.key
                    ? "bg-primary/15 text-primary"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {countsLoading ? "..." : (c ?? 0)}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function ActionList({ actions, hasSearch, onChanged, listKey }: { actions: Action[]; hasSearch: boolean; onChanged?: () => void; listKey: string }) {
  if (!actions || actions.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {hasSearch
          ? "No actions match the search. Clear the filter to see the full list."
          : "Nothing here yet — try another tab or change the filter."}
      </p>
    );
  }
  return (
    <ol className="space-y-2">
      {actions.map((a, idx) => {
        const code = String(a.code ?? a.id ?? idx);
        return (
          <li key={code}>
            <Link
              to={withFrom(`/actions/${encodeURIComponent(code)}`, listKey)}
              className="block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ActionRow action={a as unknown as Record<string, unknown>} onChanged={onChanged} />
            </Link>
          </li>
        );
      })}
    </ol>
  );
}

function PersonChips({ options, value, onChange }: {
  options: StaffOption[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs uppercase tracking-wide text-muted-foreground mr-1">By person:</span>
      <button
        type="button"
        onClick={() => onChange(ALL_PERSONS)}
        className={cn(
          "rounded-full px-2.5 py-0.5 text-xs transition-colors",
          value === ALL_PERSONS
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground hover:bg-muted/70",
        )}
      >
        All
      </button>
      {options.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onChange(s.id)}
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs transition-colors inline-flex items-center gap-1",
            value === s.id
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/70",
          )}
        >
          {s.display_name}
          {s.open_count > 0 && (
            <span className={cn(
              "rounded-full px-1.5 text-[10px] tabular-nums",
              value === s.id ? "bg-primary-foreground/20" : "bg-background/60",
            )}>
              {s.open_count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
